import { PageSerializersBooksLayout } from "types/serializers";
import { BookEditionsContolOptions } from "pages/clientverse/books/editions/index.props";
import Button from "hardcover-ui/components/Button";
import ReadingFormatIcon from "components/shared/ReadingFormatIcon";
import LocaleNumber from "components/ui/LocaleNumber";
import { FilterLanguage } from "components/EditionSelector/FilterLanguage";
import { router } from "@inertiajs/react";
import BookEditionSortButton from "./BookEditionSortButton";

export interface Props {
  book: PageSerializersBooksLayout;
  controlOptions: BookEditionsContolOptions;
}

export type GenerateEditionUrlParams = {
  languageCode?: string | null,
  readingFormatId?: string | null,
  sortBy?: string | null,
  sortDirection?: string | null
}

export default function BookEditionControls({ book, controlOptions }: Props) {
  const { editionCountsByReadingFormat, filterReadingFormatId, filterLanguage, editionLanguages } = controlOptions;
  function generateUrl({ languageCode, readingFormatId, sortBy, sortDirection }: GenerateEditionUrlParams) {
    const params = new URLSearchParams();

    if (readingFormatId) {
      params.set("filter_reading_format_id", String(readingFormatId));
    } else if (controlOptions.filterReadingFormatId) {
      params.set("filter_reading_format_id", String(controlOptions.filterReadingFormatId));
    }

    if (sortBy) {
      params.set("sort_by", sortBy);
    } else if (controlOptions.sortBy) {
      params.set("sort_by", controlOptions.sortBy);
    }

    if (sortDirection) {
      params.set("sort_direction", sortDirection);
    } else if (controlOptions.sortDirection) {
      params.set("sort_direction", controlOptions.sortDirection);
    }

    if (languageCode) {
      params.set("filter_language", languageCode);
    } else {
      // This happens when the user selects "All" in the filter.
      // Removing the parameter ensures that all languages are shown.
      params.delete("filter_language");
    }

    const queryString = params.toString();
    const url = `/books/${book.slug}/editions${queryString ? "?" + queryString : ""}`;
    return url;
  }

  function visitUrl({ languageCode, readingFormatId, sortBy, sortDirection }: GenerateEditionUrlParams) {
    router.visit(generateUrl({ languageCode, readingFormatId, sortBy, sortDirection }));
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
      <div className="flex flex-col sm:flex-row gap-4 sm:space-x-2">
        <div className="gap-2 space-x-2 items-center">
          <Button
            variant={!filterReadingFormatId ? "primary" : "tertiary"}
            size="md"
            as={{
              element: "link",
              props: {
                href: `/books/${book.slug}/editions`,
              },
            }}
          >
            <ReadingFormatIcon readingFormatId={1} size="lg" />
            <ReadingFormatIcon readingFormatId={2} size="lg" />
            <ReadingFormatIcon readingFormatId={4} size="lg" />
          </Button>

          {Object.entries(editionCountsByReadingFormat).map(([readingFormatId, count]) => (
            <Button
              key={`reading-format-${readingFormatId}`}
              variant={(Number(readingFormatId) === Number(filterReadingFormatId)) ? "primary" : "tertiary"}
              size="md"
              as={{
                element: "link",
                props: {
                  href: generateUrl({ readingFormatId }),
                },
              }}
            >
              <ReadingFormatIcon readingFormatId={Number(readingFormatId)} size="lg" />
              <LocaleNumber number={count} />
            </Button>
          ))}
        </div>

        <FilterLanguage
          codes={editionLanguages}
          value={filterLanguage}
          onChange={(languageCode) => visitUrl({ languageCode })}
          size="md"
        />
      </div>

      <BookEditionSortButton generateUrl={generateUrl} sortBy={controlOptions.sortBy} sortDirection={controlOptions.sortDirection} />
    </div>
  )
}
